import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { ASC } from 'app/shared/util/pagination.constants';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IOrder, defaultValue, ICreateOrder } from 'app/shared/model/order.model';
import { getSession } from 'app/shared/reducers/authentication';
import { BaseResponseModel } from 'app/shared/model/base-response.model';

const initialState: EntityState<IOrder> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/orders';

// Actions

export const getEntities = createAsyncThunk(
  'order/fetch_entity_list',
  async ({ page, size, sort, query }: IQueryParams & { hours?: number }) => {
    let requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}` : ''}`;
    if (query) {
      // query can contain "search:xxx", "hours:nn", "fromDate:yyyy-MM-dd", "toDate:yyyy-MM-dd"
      const parts = query.split('|');
      parts.forEach(p => {
        if (p.startsWith('search:')) requestUrl += `&search=${encodeURIComponent(p.slice(7))}`;
        else if (p.startsWith('hours:')) requestUrl += `&hours=${p.slice(6)}`;
        else if (p.startsWith('fromDate:')) requestUrl += `&fromDate=${p.slice(9)}`;
        else if (p.startsWith('toDate:')) requestUrl += `&toDate=${p.slice(7)}`;
        else requestUrl += `&search=${encodeURIComponent(p)}`;
      });
    }
    return axios.get<IOrder[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'order/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IOrder>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'order/create_entity',
  async (entity: ICreateOrder, thunkAPI) => {
    const result = await axios.post<BaseResponseModel<IOrder>>(apiUrl, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    thunkAPI.dispatch(getSession());
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'order/update_entity',
  async (entity: IOrder, thunkAPI) => {
    const result = await axios.put<BaseResponseModel<IOrder>>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'order/partial_update_entity',
  async (entity: IOrder, thunkAPI) => {
    const result = await axios.patch<BaseResponseModel<IOrder>>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'order/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IOrder>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const OrderSlice = createEntitySlice({
  name: 'order',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          loading: false,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
          entities: data.sort((a, b) => {
            if (!action.meta?.arg?.sort) {
              return 1;
            }
            const order = action.meta.arg.sort.split(',')[1];
            const predicate = action.meta.arg.sort.split(',')[0];
            return order === ASC ? (a[predicate] < b[predicate] ? -1 : 1) : b[predicate] < a[predicate] ? -1 : 1;
          }),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = OrderSlice.actions;

// Reducer
export default OrderSlice.reducer;
